import React from "react";
import {
  Box,
  Chip,
  Divider,
  InputBase,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import TollIcon from "@mui/icons-material/Toll";
import {
  WORKSPACE_AI_PROVIDERS,
  getWorkspaceProviderById,
} from "../../helper/aiEnrichment";
import { normalizeWorkspaceEnrichmentTitle } from "./savedSearchQuery";

export const WORKSPACE_ENRICH_CONTROL_HEIGHT = 48;
const MOBILE_INPUT_MAX_HEIGHT = 120;
const PLACEHOLDER = "What would you like to know about these companies?";

const MOBILE_CREDIT_CHIP_SX = {
  height: 20,
  fontSize: 10,
  fontWeight: 700,
  flexShrink: 0,
  bgcolor: "rgba(76, 175, 80, 0.12)",
  color: "#4caf50",
  "& .MuiChip-icon": { ml: "4px", color: "#4caf50" },
  "& .MuiChip-icon .MuiSvgIcon-root": {
    fontSize: "14px !important",
    color: "#4caf50",
  },
  "& .MuiChip-label": { px: 0.5 },
};

const DESKTOP_CREDIT_CHIP_SX = {
  ...MOBILE_CREDIT_CHIP_SX,
  "& .MuiChip-label": { px: 0.75 },
};

const MOBILE_PLACEHOLDER_ELLIPSIS_SX = {
  "&::placeholder": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
};

const MOBILE_SINGLE_LINE_INPUT_SX = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  ...MOBILE_PLACEHOLDER_ELLIPSIS_SX,
};

const ProviderCreditChip = ({ credits, compact = false }) => (
  <Chip
    size="small"
    icon={<TollIcon sx={{ fontSize: "14px !important" }} />}
    label={compact ? String(credits) : `${credits} credits`}
    data-testid={compact ? "enrich-credit-chip-compact" : "enrich-credit-chip-full"}
    sx={compact ? MOBILE_CREDIT_CHIP_SX : DESKTOP_CREDIT_CHIP_SX}
  />
);

export function WorkspaceEnrichProviderSelect({
  llmProvider,
  setLlmProvider,
  disabled,
  compact = false,
  isDark,
  onFocusChange,
}) {
  const selectedProvider = getWorkspaceProviderById(llmProvider);
  return (
    <Select
      value={llmProvider}
      displayEmpty
      onChange={(event) => setLlmProvider(event.target.value)}
      disabled={disabled}
      aria-label="AI Provider"
      variant="standard"
      disableUnderline
      onOpen={() => onFocusChange(true)}
      onClose={() => onFocusChange(false)}
      sx={{
        flex: "0 0 auto",
        alignSelf: compact ? "center" : "stretch",
        width: "auto",
        minWidth: compact ? 52 : { sm: 132 },
        maxWidth: compact ? 64 : { sm: 168 },
        height: WORKSPACE_ENRICH_CONTROL_HEIGHT,
        m: 0,
        p: 0,
        boxSizing: "border-box",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(247,247,248,0.95)",
        fontSize: 12.5,
        fontWeight: 600,
        color: isDark ? "rgba(255,255,255,0.85)" : "#3A3B3D",
        "& .MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          justifyContent: compact ? "center" : "flex-start",
          gap: 0.75,
          py: "0 !important",
          pl: compact ? 0.5 : 1.25,
          pr: compact ? "22px !important" : "28px !important",
          height: `${WORKSPACE_ENRICH_CONTROL_HEIGHT}px !important`,
          boxSizing: "border-box",
        },
        "& .MuiSelect-icon": {
          right: compact ? 4 : 8,
          top: "50%",
          transform: "translateY(-50%)",
          color: isDark ? "rgba(255,255,255,0.45)" : "#8A8B8E",
          fontSize: 18,
        },
      }}
      renderValue={() => compact ? (
        <ProviderCreditChip credits={selectedProvider.credits} compact />
      ) : (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0, maxWidth: "100%", height: "100%" }}>
          <Typography component="span" sx={{ fontWeight: 600, fontSize: 12.5, lineHeight: 1.2, color: "inherit", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {selectedProvider.label}
          </Typography>
          <ProviderCreditChip credits={selectedProvider.credits} />
        </Box>
      )}
      MenuProps={{ PaperProps: { sx: { borderRadius: "12px", mt: 0.5, minWidth: 260 } } }}
    >
      {WORKSPACE_AI_PROVIDERS.map((provider) => (
        <MenuItem key={provider.id} value={provider.id}>
          <Box sx={{ width: "100%", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{provider.label}</Typography>
              <Typography variant="caption" color="text.secondary">
                Uses {provider.hiddenModelLabel} behind the scenes.
              </Typography>
            </Box>
            <Chip size="small" label={`${provider.credits} credits`} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(76, 175, 80, 0.1)", color: "#4caf50" }} />
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
}

export function WorkspaceEnrichCombinedInput({
  enrichQuery,
  setEnrichQuery,
  columnTitle,
  setColumnTitle,
  llmProvider,
  setLlmProvider,
  disabled,
  titleCollision,
  isDark,
  stroke,
  focused,
  setFocused,
  multiline = false,
  compactCredits = false,
  showColumnTitle = true,
  onActivate,
  sx = {},
}) {
  return (
    <Box
      data-testid="enrich-combined-input"
      onClick={onActivate}
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        alignItems: compactCredits ? "center" : "stretch",
        height: multiline ? "auto" : WORKSPACE_ENRICH_CONTROL_HEIGHT,
        minHeight: WORKSPACE_ENRICH_CONTROL_HEIGHT,
        maxHeight: multiline ? MOBILE_INPUT_MAX_HEIGHT : WORKSPACE_ENRICH_CONTROL_HEIGHT,
        boxSizing: "border-box",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: isDark ? "rgba(22,22,22,0.92)" : "#FFFFFF",
        border: `1px solid ${focused ? "#4A2CF5" : stroke}`,
        boxShadow: focused
          ? isDark ? "0 0 0 1px #4A2CF5" : "0 0 0 1px rgba(74,44,245,0.35)"
          : "none",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        opacity: disabled ? 0.7 : 1,
        ...sx,
      }}
    >
      <InputBase
        value={enrichQuery}
        onChange={(event) => setEnrichQuery(event.target.value)}
        disabled={disabled}
        placeholder={PLACEHOLDER}
        multiline={multiline}
        minRows={multiline ? 1 : undefined}
        maxRows={multiline ? 4 : undefined}
        onFocus={() => { setFocused(true); onActivate?.(); }}
        onBlur={() => setFocused(false)}
        inputProps={{ "aria-label": PLACEHOLDER }}
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          width: "100%",
          m: 0,
          p: 0,
          display: "flex",
          alignItems: compactCredits ? "center" : multiline ? "flex-start" : "center",
          boxSizing: "border-box",
          fontSize: compactCredits ? 12.5 : 13.5,
          fontWeight: 500,
          color: isDark ? "#FFFFFF" : "#171717",
          overflow: multiline ? "auto" : "hidden",
          "& .MuiInputBase-input": {
            width: "100%",
            p: compactCredits ? (multiline ? "10px 10px !important" : "0 10px !important") : "0 12px !important",
            m: 0,
            height: multiline ? "auto !important" : `${WORKSPACE_ENRICH_CONTROL_HEIGHT}px !important`,
            minHeight: multiline ? `${WORKSPACE_ENRICH_CONTROL_HEIGHT - 2}px` : `${WORKSPACE_ENRICH_CONTROL_HEIGHT}px`,
            maxHeight: multiline ? `${MOBILE_INPUT_MAX_HEIGHT - 2}px` : `${WORKSPACE_ENRICH_CONTROL_HEIGHT}px`,
            lineHeight: 1.35,
            boxSizing: "border-box",
            display: "block",
            overflowY: multiline ? "auto" : "hidden",
            resize: "none",
            ...(compactCredits && !multiline ? MOBILE_SINGLE_LINE_INPUT_SX : {}),
            ...(compactCredits && multiline ? MOBILE_PLACEHOLDER_ELLIPSIS_SX : {}),
          },
        }}
      />
      {showColumnTitle ? (
        <>
          <Divider orientation="vertical" flexItem sx={{ borderColor: stroke }} />
          <InputBase
            value={columnTitle}
            onChange={(event) => setColumnTitle(event.target.value)}
            onBlur={() => setColumnTitle(normalizeWorkspaceEnrichmentTitle(columnTitle))}
            disabled={disabled}
            placeholder={enrichQuery}
            inputProps={{ "aria-label": "Column title (optional)", "aria-invalid": Boolean(titleCollision) }}
            sx={{ flex: "0 1 180px", minWidth: 0, px: 1.5, fontSize: 13.5, fontWeight: 500, color: isDark ? "#FFFFFF" : "#171717" }}
          />
        </>
      ) : null}
      <Divider orientation="vertical" flexItem sx={{ borderColor: stroke, alignSelf: "stretch" }} />
      <WorkspaceEnrichProviderSelect
        llmProvider={llmProvider}
        setLlmProvider={setLlmProvider}
        disabled={disabled}
        compact={compactCredits}
        isDark={isDark}
        onFocusChange={setFocused}
      />
    </Box>
  );
}
